import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../components/default/login/service/login-service/auth.service';
import { catchError, switchMap, filter, take, throwError, from } from 'rxjs';
import { API_CONFIG } from '../config/api.config';

export const authInterceptor: HttpInterceptorFn = (req, next) => {

    // ⛔ Skip static assets (i18n, images, etc.)
  if (req.url.startsWith('assets/') || req.url.includes('/assets/')) {
    return next(req);
  }
  const authService = inject(AuthService);
  
  // Skip adding token for login/register/refresh endpoints
  const isAuthEndpoint = req.url.includes('/login') || 
                         req.url.includes('/register') || 
                         req.url.includes('/refresh');
  
  // Handle base URL - prepend baseUrl if URL is relative and not absolute
  let requestUrl = req.url;
  if (!API_CONFIG.isAbsoluteUrl(requestUrl)) {
    // Only prepend baseUrl if it's not already included
    const baseUrl = API_CONFIG.getBaseUrl();
    if (baseUrl && !requestUrl.startsWith(baseUrl)) {
      // Ensure baseUrl doesn't end with / and requestUrl doesn't start with /
      const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const cleanRequestUrl = requestUrl.startsWith('/') ? requestUrl : `/${requestUrl}`;
      requestUrl = `${cleanBaseUrl}${cleanRequestUrl}`;
    }
  }
  
  // Get token asynchronously and add to request
  return from(authService.getToken()).pipe(
    switchMap(token => {
      // Clone the request with updated URL and add the authorization header if token exists
      let authReq = req.clone({
        url: requestUrl
      });
     
      if (token && !isAuthEndpoint) {
        authReq = authReq.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        });
      }
      
      // Execute the request and handle errors
      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          // Handle 401 Unauthorized errors with TOKEN_EXPIRED code - try to refresh token
          if (error.status === 401 && !isAuthEndpoint) {
            // Check if error response contains TOKEN_EXPIRED code
            // Response structure: { success: false, error: { code: "TOKEN_EXPIRED", message: "...", details: {} } }
            const errorBody = error.error;
            
            // Check multiple possible error response structures
            // Primary check: error.error.code (matches the actual API response structure)
            const errorCode = errorBody?.error?.code || 
                             errorBody?.code || 
                             errorBody?.data?.code;
            
            // Also check message for "Token has expired" or "TOKEN_EXPIRED" string
            const errorMessage = errorBody?.error?.message || errorBody?.message || '';
            const hasTokenExpiredMessage = errorMessage.includes('Token has expired') || 
                                         errorMessage.includes('TOKEN_EXPIRED');
            
            // Fallback: check if the stringified error body contains TOKEN_EXPIRED
            const errorBodyString = JSON.stringify(errorBody || {});
            const hasTokenExpiredInString = errorBodyString.includes('TOKEN_EXPIRED');
            
            const isTokenExpired = errorCode === 'TOKEN_EXPIRED' ||
                                   hasTokenExpiredMessage ||
                                   hasTokenExpiredInString;
            
            console.log('🔍 401 Error detected:', {
              status: error.status,
              url: requestUrl,
              isAuthEndpoint,
              errorBody: errorBody,
              errorCode: errorCode,
              errorMessage: errorMessage,
              hasTokenExpiredInString: hasTokenExpiredInString,
              isTokenExpired: isTokenExpired
            });
            
            if (isTokenExpired) {
              console.log('✅ Token expired detected, attempting to refresh token...');
              console.log('🔄 Calling refreshAccessToken()...');
              
              // If refresh is already in progress, wait for it and retry
              if (authService.isRefreshingToken()) {
                return authService.getRefreshTokenSubject().pipe(
                  filter(token => token !== null),
                  take(1),
                  switchMap(newToken => {
                    if (!newToken) {
                      // Refresh failed, logout user
                      authService.logout();
                      return throwError(() => error);
                    }
                    // Retry the original request with the new token
                    const retryReq = req.clone({
                      url: requestUrl,
                      setHeaders: {
                        Authorization: `Bearer ${newToken}`
                      }
                    });
                    return next(retryReq);
                  })
                );
              } else {
                // Start token refresh process - pass the expired token
                // Get the expired token from the original request or from storage
                return from(authService.getToken()).pipe(
                  switchMap(expiredToken => {
                    console.log('📤 Passing expired token to refresh API:', expiredToken ? 'Token found' : 'No token');
                    // Call refresh with the expired token
                    return authService.refreshAccessToken(expiredToken).pipe(
                      switchMap(newToken => {
                        // Retry the original request with the new token
                        const retryReq = req.clone({
                          url: requestUrl,
                          setHeaders: {
                            Authorization: `Bearer ${newToken}`
                          }
                        });
                        return next(retryReq);
                      }),
                      catchError(refreshError => {
                        // If refresh fails, the error will be thrown and user will be logged out
                        // (handled in authService.refreshAccessToken)
                        console.error('Token refresh failed:', refreshError);
                        return throwError(() => refreshError);
                      })
                    );
                  })
                );
              }
            } else {
              // 401 error but not TOKEN_EXPIRED - could be invalid token or other auth issue
              console.warn('401 error but not TOKEN_EXPIRED, logging out user');
              authService.logout();
              return throwError(() => error);
            }
          }
          
          // For other errors (including 403) or auth endpoints, just pass through the error
          // 403 errors will be handled by components/services to display the API response on UI
          return throwError(() => error);
        })
      );
    })
  );
};

